// Angular imports
import {
	Component,
	HostListener,
	OnDestroy,
	OnInit,
	ViewChild,
} from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { Observable, Subject, forkJoin, of, takeUntil } from "rxjs";

// Models
import { ContextLocationViewModel } from "src/app/_core/models/main/context-location.model";
import { LinkItem } from "src/app/_core/models/main/link-item.model";
import { Policies } from "src/app/_core/models/main/policies.enum";
import { getHomeMenu } from "src/app/_core/constants/menu.constants";

// Services
import {
	FormBuilder,
	FormControl,
	FormGroup,
	Validators,
} from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatTabGroup } from "@angular/material/tabs";
import { TranslateService } from "@ngx-translate/core";
import { CebeProcedureViewModel } from "src/app/_core/models/centros/cebe-procedure.model";
import { CebeViewModel } from "src/app/_core/models/centros/cebe.model";
import { CebesCentrosViewModel } from "src/app/_core/models/centros/centro-list-base.model";
import { CentrosViewModel } from "src/app/_core/models/centros/centro.model";
import { EmpleadoViewModel } from "src/app/_core/models/centros/empleado.model";
import { IdCodigoDescripcion } from "src/app/_core/models/centros/idCodigoDescripcion.model";
import {
	CebesCentrosService,
	ContratoLookup,
} from "src/app/_core/providers/cebes-centros.service";
import { CebesService } from "src/app/_core/providers/cebes.service";
import { CentrosService } from "src/app/_core/providers/centros.service";
import {
	GlobalService,
	MigaDePan,
} from "src/app/_core/providers/global.service";
import { ImportService } from "src/app/_core/providers/import.service";
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";
import { BreadcrumbService } from "src/app/_core/services/breadcrumb.service";
import { CanComponentDeactivate } from "src/app/_core/guards/unsaved-changes.guard";
import { DialogService } from "src/app/_core/services/dialog.service";
import {
	AccesosService,
	TipoElementoAcceso,
} from "src/app/_core/providers/accesos.service";
import { UserProfileStore } from "src/app/_core/stores/user-profile.store";
import {
	SolicitudDatosDialogComponent,
	SolicitudDatosDialogData,
} from "src/app/modules/shared/components/solicitud-datos-dialog/solicitud-datos-dialog.component";
import { CentroDesignDataCardComponent } from "../../components/centros/centro-design-data-card/centro-design-data-card.component";
import { CentroDetailsCardComponent } from "../../components/centros/centro-details-card/centro-details-card.component";
import { CentroOperationDataCardComponent } from "../../components/centros/centro-operation-data-card/centro-operation-data-card.component";
import { CentroUserReportCardComponent } from "../../components/centros/centro-user-report-card/centro-user-report-card.component";

@Component({
    selector: "app-centro-page",
    templateUrl: "./centro.page.html",
    styleUrls: ["./centro.page.scss"],
    standalone: false
})
export class CentroPageComponent
	implements OnInit, OnDestroy, CanComponentDeactivate
{
	// Properties
	@ViewChild("CentroDetailsCardComponentRef")
	centroDetailsComponent: CentroDetailsCardComponent;
	@ViewChild("CentroUserReportCardComponentRef")
	centroUserReportComponent: CentroUserReportCardComponent;
	@ViewChild("CentroDesignDataCardComponentRef")
	centroDesignDataComponent: CentroDesignDataCardComponent;
	@ViewChild("CentroOperationDataCardComponentRef")
	centroOperationDataComponent: CentroOperationDataCardComponent;
	@ViewChild("tabGroup") tabGroup: MatTabGroup;
	@ViewChild("fileInputUsers") fileInput: any;

	/**
	 * Pestaña de Personas: un responsable fijo se gestiona en SU pestaña del propio detalle
	 * (no se enruta). PuestoId sintético -1 = responsable del centro (Información general);
	 * el resto (-10x) son roles del Reporte de Sostenibilidad.
	 */
	goToResponsableTab(contact: { puestoId?: number }): void {
		if (this.tabGroup) {
			this.tabGroup.selectedIndex = contact?.puestoId === -1 ? 0 : 1;
		}
	}

	/**
	 * Deep-link a una pestaña vía queryParam `tab` (p. ej. /sites/14365?tab=energia,
	 * usado por la tab de energía del contrato y por /energy). setTimeout: el tabGroup
	 * se pinta tras cargar el centro (los mat-tab son condicionales).
	 * Índices en MODO LECTURA con centro cargado: 0 General · 1 Sostenibilidad ·
	 * 2 Diseño · 3 Operación · 4 Personas · 5 ENERGÍA (mantener si se reordenan tabs).
	 */
	private seleccionarTabDeQueryParam(): void {
		const tab = this._route.snapshot.queryParamMap.get("tab");
		if (tab !== "energia") {
			return;
		}
		setTimeout(() => {
			if (this.tabGroup && !this.isEditing && !this.isCreating) {
				this.tabGroup.selectedIndex = 5;
			}
		});
	}

	responsableControl: FormControl = new FormControl([Validators.required]);
	responsableReporteControl: FormControl = new FormControl();
	introductorAmbientalControl: FormControl = new FormControl();
	introductorSocialControl: FormControl = new FormControl();
	validadorPrincipalControl: FormControl = new FormControl();
	validadorApoyoControl: FormControl = new FormControl();
	cebeControl: FormControl = new FormControl();
	mainFormGroup: FormGroup;

	public cebesCentros: CebeProcedureViewModel[] = [];
	public cebe: CebeProcedureViewModel;
	cebesSimples: IdCodigoDescripcion[] = [];

	public employees: EmpleadoViewModel[] = [];

	private selectedFilesUsers: File | null = null;
	public centro: CentrosViewModel;
	public cebeCentro: CebesCentrosViewModel;
	public isCreating: boolean = false;
	/** Edición inline: solo lectura por defecto; true al pulsar Editar (o en alta). */
	public isEditing: boolean = false;
	/** Permiso real de edición del centro (se conserva al alternar modo). */
	public canEditCentro: boolean = false;
	/** Activo durante la recarga de "descartar", para que el guard no bloquee. */
	private discarding: boolean = false;

	lastTabIndex: number;
	validatedCentroDetailsForm: boolean;
	validatedUsuariosReportForm: boolean = false;
	validatedDesignForm: boolean;
	validatedOperationForm: boolean;
	centroOperationData: FormGroup;
	centroReportData: FormGroup;
	centroDesignData: FormGroup;
	centroDetailData: FormGroup;

	public cebeId: number = 0;
	public cebePadre: CebeViewModel;
	public cebesimple: IdCodigoDescripcion;
	/** Contrato padre del centro (vínculo fijo; lookup de contexto no scopeado). */
	public contratoPadre: ContratoLookup | null = null;
	// Attributes
	private readonly destroy$ = new Subject<void>();
	contextLocation: ContextLocationViewModel;

	private readonly menu: LinkItem[] = getHomeMenu();
	constructor(
		private readonly _titleService: Title,
		private readonly _translateService: TranslateService,
		private readonly _route: ActivatedRoute,
		private readonly _centrosService: CentrosService,
		private readonly _cebesService: CebesService,
		private readonly _masterService: MasterService,
		private readonly _cebesCentrosService: CebesCentrosService,
		private readonly userProfileStore: UserProfileStore,
		private readonly router: Router,
		private readonly fb: FormBuilder,
		private readonly _importService: ImportService,
		private readonly _notificationService: NotificationService,
		private readonly _globalService: GlobalService,
		private readonly _breadcrumbService: BreadcrumbService,
		private readonly _dialogService: DialogService,
		private readonly _accesosService: AccesosService,
		private readonly _dialog: MatDialog,
	) {}

	// ── Menú "Utilidades" (dropdown propio .utils-menu del ds) ───────────────
	utilsMenuOpen = false;

	toggleUtilsMenu(): void {
		this.utilsMenuOpen = !this.utilsMenuOpen;
	}

	closeUtilsMenu(): void {
		this.utilsMenuOpen = false;
	}

	/** Cierre al click fuera del contenedor del menú (.utils-wrap). */
	@HostListener("document:click", ["$event"])
	onDocumentClick(event: MouseEvent): void {
		if (
			this.utilsMenuOpen &&
			!(event.target as HTMLElement)?.closest(".utils-wrap")
		) {
			this.closeUtilsMenu();
		}
	}

	/** ¿Puede pedir la cumplimentación de datos? (policy App.Solicitud.Cumplimentacion). */
	get canSolicitarDatos(): boolean {
		return this.userProfileStore.hasPolicy(
			Policies.Solicitud_Cumplimentacion,
		);
	}

	/**
	 * ¿Puede gestionar la energía (CUPS) de este centro? Gestor de energía global
	 * (App.Energia.Manage) O edición del centro (Manage/responsable). El back revalida.
	 */
	get canManageEnergia(): boolean {
		return (
			this.userProfileStore.hasPolicy(Policies.Energia_Manage) ||
			this.canEditCentro
		);
	}

	/** Abre el diálogo compartido "Solicitar datos" sobre este centro. */
	openSolicitudDatos(): void {
		if (!this.centro?.id) {
			return;
		}
		this._dialog.open(SolicitudDatosDialogComponent, {
			width: "560px",
			maxWidth: "92vw",
			autoFocus: false,
			restoreFocus: true,
			panelClass: "ds-dialog-panel",
			backdropClass: "ds-dialog-backdrop",
			data: {
				entityType: "Centro",
				entityId: this.centro.id,
				entityCodigo: this.centro.codigo,
				entityNombre: this.centro.descripcion || this.centro.codigo,
			} satisfies SolicitudDatosDialogData,
		});
	}

	/**
	 * Imagen de cabecera como data URL. Prioriza el valor del formulario (preview en
	 * vivo al subir/cambiar antes de guardar); si no, la del centro cargado.
	 */
	get coverImageSrc(): string | null {
		const b64: string | null =
			this.centroDetailsComponent?.myDetailForm?.get("imageDataB64")
				?.value ?? this.centro?.imageDataB64;
		if (!b64) {
			return null;
		}
		return b64.startsWith("data:") ? b64 : `data:image/jpeg;base64,${b64}`;
	}

	/** Controles/formularios asociados a cada pestaña. Incluye los controles de
	 * persona SUELTOS (responsable, sostenibilidad), que no viven en los FormGroup
	 * de las cards — sin ellos el dirty/invalid de la pestaña no los vería. */
	private tabControls(
		index: number,
	): Array<{ dirty?: boolean; invalid?: boolean } | null | undefined> {
		switch (index) {
			case 0:
				return [
					this.centroDetailsComponent?.myDetailForm,
					this.responsableControl,
				];
			case 1:
				return [
					this.mainFormGroup?.get("centroUserReport") ??
						this.mainFormGroup,
					this.responsableReporteControl,
					this.introductorAmbientalControl,
					this.introductorSocialControl,
					this.validadorPrincipalControl,
					this.validadorApoyoControl,
				];
			case 2:
				return [this.centroDesignDataComponent?.myDesignForm];
			case 3:
				return [this.centroOperationDataComponent?.myOperationForm];
			default:
				return [];
		}
	}

	/** ¿La pestaña tiene cambios sin guardar? (marcador visual, solo edición). */
	isTabDirty(index: number): boolean {
		return (
			(this.isEditing || this.isCreating) &&
			this.tabControls(index).some((c) => c?.dirty)
		);
	}

	// ── Meta del hero (chips de contexto) ─────────────────────────────────
	/** Descripción en vivo: el form mientras se edita; si no, lo guardado. */
	get heroDescripcion(): string | null {
		const formVal = this.centroDetailsComponent?.myDetailForm?.get(
			"descripcion",
		)?.value;
		return (
			(this.isEditing || this.isCreating ? formVal : null) ??
			this.centro?.descripcion ??
			null
		);
	}

	/** Estado activo en vivo: el form mientras se edita; si no, lo guardado. */
	get heroActivo(): boolean {
		if (this.isEditing || this.isCreating) {
			const formVal =
				this.centroDetailsComponent?.myDetailForm?.get("activo")?.value;
			if (formVal !== null && formVal !== undefined) {
				return !!formVal;
			}
		}
		return !!this.centro?.activo;
	}

	/** Tipo de instalación resuelto por la card de detalle ("—" = sin valor). */
	get heroTipoInstalacion(): string | null {
		const label = this.centroDetailsComponent?.tipoInstalacionLabel;
		return label && label !== "—" ? label : null;
	}

	/** Centro de actividad resuelto por la card de detalle ("—" = sin valor). */
	get heroCentroActividad(): string | null {
		const label = this.centroDetailsComponent?.centroActividadLabel;
		return label && label !== "—" ? label : null;
	}

	/** Navega al contrato padre desde el chip del hero (guard de cambios aplica). */
	goToContratoPadre(): void {
		if (this.contratoPadre?.id) {
			this.router.navigate(["/contracts", this.contratoPadre.id]);
		}
	}

	/** ¿La pestaña tiene errores de validación sin resolver? (solo edición:
	 * la etiqueta sustituye su icono por un warning mientras duren). */
	isTabInvalid(index: number): boolean {
		return (
			(this.isEditing || this.isCreating) &&
			this.tabControls(index).some((c) => c?.invalid)
		);
	}

	/** ¿Hay cambios sin guardar en alguna pestaña? */
	get hasUnsavedChanges(): boolean {
		return [0, 1, 2, 3].some((i) =>
			this.tabControls(i).some((c) => c?.dirty),
		);
	}

	/** Guard de navegación (CanDeactivate): avisa de cambios sin guardar. */
	canDeactivate(): boolean | Observable<boolean> {
		if (this.discarding || !this.hasUnsavedChanges) {
			return true;
		}
		return this._dialogService.confirm({
			type: "danger",
			title: this._translateService.instant("common.unsavedChanges.title"),
			message: this._translateService.instant("centro.edit.leaveConfirm"),
			confirmText: this._translateService.instant("common.unsavedChanges.leave"),
			cancelText: this._translateService.instant("common.unsavedChanges.stay"),
		});
	}

	/** Aviso del navegador al cerrar/recargar con cambios sin guardar. */
	@HostListener("window:beforeunload", ["$event"])
	onBeforeUnload(event: BeforeUnloadEvent): void {
		if (this.hasUnsavedChanges) {
			event.preventDefault();
			event.returnValue = "";
		}
	}

	/** Entra en modo edición (solo con permiso). */
	enterEditMode(): void {
		if (!this.canEditCentro) {
			return;
		}
		this.isEditing = true;
	}

	/** Cancela la edición. Si hay cambios, confirma con un diálogo antes de descartar. */
	cancelEditMode(): void {
		if (!this.hasUnsavedChanges) {
			// Sin cambios: no hay nada que revertir → solo cambiar de modo
			// (en alta no existe modo lectura, así que se vuelve al listado).
			if (this.isCreating) {
				this.router.navigate(["/sites"]);
			} else {
				this.isEditing = false;
			}
			return;
		}
		this._dialogService
			.confirm({
				type: "danger",
				icon: "report",
				title: this._translateService.instant("common.discard"),
				message: this._translateService.instant("centro.edit.discardConfirm"),
				confirmText: this._translateService.instant("common.discard"),
				cancelText: this._translateService.instant("common.keepEditing"),
				confirmTone: "danger",
			})
			.subscribe((confirmed) => {
				if (confirmed) {
					this.applyCancelCentro();
				}
			});
	}

	/** Restaura el estado original (recarga limpia) y sale del modo edición. */
	private applyCancelCentro(): void {
		const id = this.centro?.id;
		this.isEditing = false;
		this.discarding = true; // evita que el guard pida confirmación al recargar
		if (id) {
			this.router
				.navigateByUrl("/sites", { skipLocationChange: true })
				.then(() => this.router.navigate(["/sites", id]));
		} else {
			this.router.navigate(["/sites"]);
		}
	}

	/** Vuelve al listado de centros. */
	onBack(): void {
		this.router.navigate(["/sites"]);
	}

	// Events
	ngOnInit(): void {
		this._globalService.setHeaderOptions(
			[],
			this.contextLocation,
			[],
			this.menu,
		);

		this.router.events.subscribe((event) => {
			if (event instanceof NavigationEnd) {
			}
		});

		this._route.params
			.pipe(takeUntil(this.destroy$))
			.subscribe((params) => {
				this._titleService.setTitle(
					this._translateService.instant("title.detail.centro"),
				);

				this.cebeId = Number(
					this._route.snapshot.paramMap.get("cebeId"),
				);
				const centroId = Number(
					this._route.snapshot.paramMap.get("centroId"),
				);

				this.contextLocation = {
					centro: this._route.snapshot.params["centroId"],
				};

				forkJoin({
					employees: this._masterService.getEmpleados(),
					centro: centroId
						? this._centrosService.getCentroById(centroId)
						: of(null),

					cebeCentro: centroId
						? this._cebesCentrosService.getContratoCentroByCentro(
								centroId,
							)
						: of(null),

					cebe:
						this.cebeId !== 0
							? this._cebesService.getCebeLookupById(this.cebeId)
							: of(null),
				})
					.pipe(takeUntil(this.destroy$))
					.subscribe(({ employees, centro, cebeCentro, cebe }) => {
						this.employees = employees;
						this.cebeCentro = cebeCentro;

						if (cebe) {
							this.cebePadre = cebe;

							this.cebesimple = {
								id: cebe.id,
								codigo: cebe.codigo,
								descripcion: cebe.descripcion,
							};
							this.cebesSimples.push(this.cebesimple);
							const partes = window.location.href
								.split("/")
								.filter((p) => p !== "");

							const miga: MigaDePan = {
								nombre: `(${this.cebePadre?.codigo}) ${this.cebePadre?.descripcion}`,
								url: `${partes[0]}//${partes[1]}/#/cebe/${this.cebePadre?.id}`,
							};

							this._globalService.agregarCebeToMiga(miga);
						} else {
							// El "padre" del centro es su CONTRATO (vínculo fijo: el código del
							// centro deriva del código del contrato). Se recupera vía lookup de
							// CONTEXTO no scopeado (quien accede al centro ve la info básica del
							// contrato aunque no esté en su alcance). Antes se pasaba el id de
							// contrato a /CebeOperativo/LookupById → 404 sistemático.
							this._cebesCentrosService
								.getContratoLookupById(cebeCentro.contratoID)
								.subscribe((s) => {
									if (s) {
										this.contratoPadre = s;
										// El país del contrato es el fallback del país del centro
										// (sin CeBe vinculado): re-aplicar validadores Provincia/Región.
										this.centroDetailsComponent?.aplicarValidadoresPais();
										const partes = window.location.href
											.split("/")
											.filter((p) => p !== "");

										const miga: MigaDePan = {
											nombre: `(${s.codigo}) ${s.descripcion}`,
											url: `${partes[0]}//${partes[1]}/#/contracts/${s.id}`,
										};

										this._globalService.agregarCebeToMiga(miga);
									}
									const miga1: MigaDePan = {
										nombre: `(${centro?.codigo}) ${centro?.descripcion}`,
										url: window.location.href,
									};

									this._globalService.agregarCentroToMiga(
										miga1,
									);
								});
						}
						if (centro) {
							this.centro = centro;
							this.canEditCentro = !!centro.edicionCentro;
							this.isEditing = false;
							this._breadcrumbService.setDetail(
								centro.descripcion || centro.codigo,
							);

							this._accesosService
									.registrar(TipoElementoAcceso.Centro, centro.id)
									.subscribe();
								this.mainFormGroup = this.initMainFormGroup();
								this.seleccionarTabDeQueryParam();
						} else {
							this.isCreating = true;
							this.isEditing = true;
							this.initCreateMode();
						}

						this.loadCebesCentro();
					});
			});
	}

	ngOnDestroy(): void {
		this._breadcrumbService.clear();
		this.destroy$.next();
		this.destroy$.complete();
	}

	/** Preselecciona los controles de persona a partir de los IDs del centro.
	 * (Las fotos las resuelven los propios componentes vía EmpleadoFotoService.) */
	async loadValues(): Promise<void> {
		if (!this.isCreating) {
			this.responsableControl.setValue(
				this.employees.find((f) => f.id == this.centro.responsableID),
			);
			this.responsableReporteControl.setValue(
				this.employees.find(
					(f) => f.id == this.centro.responsableReporteID,
				),
			);
			this.introductorAmbientalControl.setValue(
				this.employees.find(
					(f) => f.id == this.centro.introductorAmbientalID,
				),
			);
			this.introductorSocialControl.setValue(
				this.employees.find(
					(f) => f.id == this.centro.introductorSocialID,
				),
			);
			this.validadorPrincipalControl.setValue(
				this.employees.find(
					(f) => f.id == this.centro.validadorPrincipalID,
				),
			);
			this.validadorApoyoControl.setValue(
				this.employees.find((f) => f.id == this.centro.validadorApoyoID),
			);

			// Recarga = estado limpio: sin esto, una selección previa dejaría los
			// controles de persona dirty para siempre (falso marcador de pestaña).
			[
				this.responsableControl,
				this.responsableReporteControl,
				this.introductorAmbientalControl,
				this.introductorSocialControl,
				this.validadorPrincipalControl,
				this.validadorApoyoControl,
			].forEach((c) => c.markAsPristine());

			this.reportFormStatus();
		}
	}

	loadCebesCentro(): void {
		//if (!this.isCreating) {
		//let cebesCentro: CebeProcedureViewModel = this.cebesCentros.find(f => f.id == this.cebeCentro?.cebeID);
		this.cebeControl.setValue(this.cebesSimples[0]?.id);
		//}
	}

	backTab() {
		this.formChanged(0);
	}

	forwardTab() {
		this.formChanged(1);
	}

	detailsFormStatus(status: boolean) {
		this.responsableControl.valueChanges.forEach((value) => {
			if (status && value?.id) {
				this.validatedCentroDetailsForm = true;
			} else if (!value) {
				this.validatedCentroDetailsForm = false;
			} else {
				this.validatedCentroDetailsForm = status;
			}
		});
	}

	detailsFormValue(value: FormGroup) {
		this.centroDetailData = value;
	}

	reportFormStatus() {
		let countFilled = 0;

		if (this.responsableReporteControl.value?.id) {
			countFilled++;
		}
		if (this.introductorAmbientalControl.value?.id) {
			countFilled++;
		}
		if (this.introductorSocialControl.value?.id) {
			countFilled++;
		}
		if (this.validadorPrincipalControl.value?.id) {
			countFilled++;
		}
		if (this.validadorApoyoControl.value?.id) {
			countFilled++;
		}

		if (countFilled !== 0 && countFilled === 5) {
			this.validatedUsuariosReportForm = true;
		} else {
			this.validatedUsuariosReportForm = false;
		}
	}

	designFormStatus(status: boolean) {
		this.validatedDesignForm = status;
	}

	designFormValues(value: FormGroup) {
		this.centroDesignData = value;
	}

	operationFormStatus(status: boolean) {
		this.validatedOperationForm = status;
	}

	operationFormValues(value: FormGroup) {
		this.centroOperationData = value;
	}

	/** Update del centro tras sincronizar sus CeBe (encadenado desde submitForm). */
	private persistCentroUpdate(formData: CentrosViewModel): void {
		this._centrosService
			.updateCentro(formData, this.centro.id)
			.subscribe((result) => {
				if (result) {
					this.centro = result;
					this.ngOnInit();
				}
			});
	}

	submitForm(): Observable<boolean> {
		if (
			this.mainFormGroup.valid &&
			this.centroOperationData.valid &&
			this.centroDetailData.valid &&
			this.centroDesignData.valid
		) {
			const formData = this.mapFormValuesToModel(this.mainFormGroup.value);
			if (this.centro.id) {
				// Persistir los CeBe vinculados ANTES del update del centro: la validación backend
				// del tipo de instalación (unión de ternas) necesita ver los CeBe ya vinculados.
				const cebeIds: number[] =
					this.centroDetailData?.get("cebeIds")?.value ?? [];
				this._cebesService
					.setCebesDeCentro(this.centro.id, cebeIds)
					.subscribe({
						next: () => this.persistCentroUpdate(formData),
						error: () => this.persistCentroUpdate(formData),
					});
				this._notificationService.showSuccess(
					this._translateService.instant(
						"messages.savedSuccessfully",
					),
					null,
				);
				return of(true);
			}

			// Alta de centro RETIRADA por esta vía (camino A huérfano, ADR-0013 / §1.ter):
			// el alta es SOLO desde el contrato (contracts/:id/sites/new -> CreateForContrato).
			// Este componente ya solo sirve el detalle/edición (sites/:centroId), donde
			// this.centro.id siempre está presente; sin id no hay operación.
			return of(false);
		} else {
			this.markFormGroupAsTouched(this.mainFormGroup);

			this._notificationService.showError(
				this._translateService.instant("messages.errorsWhileSaving"),
			);

			return of(false);
		}
	}

	redirigirDetalle(centroId: number): void {
		this.router.navigate(["/sites", centroId]);
	}

	/** Código corto PERSISTIDO del centro (ADR-0016): fijado en el alta, inmutable. */
	get codigoCortoCentro(): string | null {
		return this.centro?.codigoCorto ?? null;
	}

	/** Copia un código al portapapeles (mismo patrón que el detalle de contrato). */
	async copyCodigo(value?: string | null): Promise<void> {
		if (!value) {
			return;
		}
		try {
			await navigator.clipboard.writeText(value);
			this._notificationService.showInfo(
				this._translateService.instant("contracts.codes.copied"),
			);
		} catch {
			// Portapapeles no disponible (permisos/contexto no seguro): sin ruido.
		}
	}

	formChanged(direction: number) {
		const currentTabIndex = this.tabGroup?.selectedIndex;
		this.lastTabIndex = this.tabGroup?._tabs.length - 1;

		if (direction === 0) {
			this.navigateToBackTab(currentTabIndex);
		} else {
			this.navigateToNextTab(currentTabIndex);
		}
	}

	saveonly() {
		this.submitForm().subscribe((success) => {
			if (success) {
			}
		});
	}

	// Selección de personas (app-user-search .ds): el componente emite el
	// EmpleadoViewModel (o null si se limpia con la X) y muestra su avatar/foto solo.
	// setValue programático NO marca dirty → se marca a mano (marcador de pestaña).
	onResponsableSelected(empleado: EmpleadoViewModel | null) {
		this.responsableControl.setValue(empleado ?? null);
		this.responsableControl.markAsDirty();
	}

	onReporteSelected(empleado: EmpleadoViewModel | null) {
		this.responsableReporteControl.setValue(empleado ?? null);
		this.responsableReporteControl.markAsDirty();
		this.reportFormStatus();
	}

	onAmbientalSelected(empleado: EmpleadoViewModel | null) {
		this.introductorAmbientalControl.setValue(empleado ?? null);
		this.introductorAmbientalControl.markAsDirty();
		this.reportFormStatus();
	}

	onSocialSelected(empleado: EmpleadoViewModel | null) {
		this.introductorSocialControl.setValue(empleado ?? null);
		this.introductorSocialControl.markAsDirty();
		this.reportFormStatus();
	}

	onValPrincipalSelected(empleado: EmpleadoViewModel | null) {
		this.validadorPrincipalControl.setValue(empleado ?? null);
		this.validadorPrincipalControl.markAsDirty();
		this.reportFormStatus();
	}

	onValApoyoSelected(empleado: EmpleadoViewModel | null) {
		this.validadorApoyoControl.setValue(empleado ?? null);
		this.validadorApoyoControl.markAsDirty();
		this.reportFormStatus();
	}

	onUploadUserDataFiles(): void {
		this.fileInput.nativeElement.click();
	}

	onFileUsersChange(event: any): void {
		const files: FileList = event.target.files;

		if (files && files.length > 0) {
			this.selectedFilesUsers = files.item(0);
			this.uploadFiles();
		}
	}

	uploadFiles(): void {
		if (this.selectedFilesUsers) {
			this._importService.uploadUsers(this.selectedFilesUsers).subscribe(
				(response: string) => {
					const blob = new Blob([response], { type: "text/plain" });
					const url = window.URL.createObjectURL(blob);
					const a = document.createElement("a");
					a.href = url;
					a.download = `resultadoCarga_${this.selectedFilesUsers.name}.txt`;
					document.body.appendChild(a);
					a.click();
					document.body.removeChild(a);
					window.URL.revokeObjectURL(url);
				},
				(error) => {
					console.error("Error uploading users:", error);
				},
			);
		}
	}

	public getUserLocale(): string {
		let locale: string = "en-US";
		const userProfile = this.userProfileStore.getUserProfile();
		if (userProfile && userProfile.preferredLang) {
			if (userProfile.preferredLang == "es") {
				locale = "es-ES";
			} else if (userProfile.preferredLang == "pl") {
				locale = "pl-PL";
			}
		}
		return locale;
	}

	// Private methods
	private markFormGroupAsTouched(formGroup: FormGroup): void {
		Object.values(formGroup.controls).forEach((control) => {
			if (control instanceof FormControl) {
				control.markAsTouched();
			} else if (control instanceof FormGroup) {
				this.markFormGroupAsTouched(control);
			}
		});
	}

	/**
	 * Modelo data-driven de visibilidad por tipo de instalación (sustituye al antiguo
	 * toggling de clases .hidden por querySelector, que además estaba ROTO en la tab
	 * de Diseño). Las cards consultan `isInstFieldVisible(clave)` con *ngIf.
	 */
	visibleInstFields = new Set<string>();

	isInstFieldVisible(key: string): boolean {
		return this.visibleInstFields.has(key);
	}

	anyInstFieldVisible(keys: string[]): boolean {
		return keys.some((k) => this.visibleInstFields.has(k));
	}

	showHideInstallationFields(): void {
		const installationTypeId =
			this.centroDetailData.get("tipoInstalacionID");

		const selectedInstall =
			this.centroDetailsComponent.tipoInstalacionList.filter(
				(i) => i.id == installationTypeId.value,
			);

		this.hideAllOperacionDisenoFields();

		if (selectedInstall && selectedInstall.length !== 0) {
			const actividadID = selectedInstall[0].actividadID;
			const tipoProyectoID = selectedInstall[0].tipoProyectoID;
			const instalacionID = selectedInstall[0].id;

			let alcanceProyecto,
				caudalDiseno,
				numAbonados,
				kmRedAbas,
				kmRedSanea,
				habitantesEquivalentes;
			let consumoEnergeticoAnual,
				consumoEnergeticoAutoproducido,
				bonifPenalEnergetica,
				desnitrificacion,
				digestAnaerobea,
				recuperacionFosforo,
				iLI,
				prodAgua,
				volAguaDist,
				volCaudalRecog,
				usoPriPorc,
				usoPriAguaProd,
				usoSecPorc,
				usoSecAguaProd,
				usoPriPorcRege,
				usoPriAguaRege,
				poblaAbaste;

			alcanceProyecto =
				caudalDiseno =
				numAbonados =
				iLI =
				kmRedAbas =
				kmRedSanea =
				habitantesEquivalentes =
					false;
			consumoEnergeticoAnual =
				consumoEnergeticoAutoproducido =
				bonifPenalEnergetica =
				desnitrificacion =
				digestAnaerobea =
				recuperacionFosforo =
				prodAgua =
				volAguaDist =
				volCaudalRecog =
				usoPriPorc =
				usoPriAguaProd =
				usoSecPorc =
				usoSecAguaProd =
				usoPriPorcRege =
				usoPriAguaRege =
				poblaAbaste =
					false;

			switch (actividadID) {
				case 1: // Diseño y Construcción
					if (tipoProyectoID === 9 && instalacionID === 17) {
						// EBAR EBAR
						alcanceProyecto =
							caudalDiseno =
							habitantesEquivalentes =
								true;
						consumoEnergeticoAnual =
							consumoEnergeticoAutoproducido =
							bonifPenalEnergetica =
								true;
					}
					if (tipoProyectoID === 7 && instalacionID === 14) {
						// EDAR EDAR
						alcanceProyecto =
							caudalDiseno =
							habitantesEquivalentes =
								true;
						consumoEnergeticoAnual =
							consumoEnergeticoAutoproducido =
							bonifPenalEnergetica =
							desnitrificacion =
							digestAnaerobea =
							recuperacionFosforo =
							usoPriPorcRege =
							usoPriAguaRege =
								true;
					}
					if (tipoProyectoID === 8 && instalacionID === 32) {
						// ETAP ETAP
						alcanceProyecto = caudalDiseno = true;
						consumoEnergeticoAnual =
							consumoEnergeticoAutoproducido =
							bonifPenalEnergetica =
							usoPriPorc =
							usoPriAguaProd =
							usoSecPorc =
							usoSecAguaProd =
							poblaAbaste =
								true;
					}
					if (tipoProyectoID === 11 && instalacionID === 33) {
						// IDAM IDAM
						alcanceProyecto = caudalDiseno = true;
						consumoEnergeticoAnual =
							consumoEnergeticoAutoproducido =
							bonifPenalEnergetica =
							usoPriPorc =
							usoPriAguaProd =
							usoSecPorc =
							usoSecAguaProd =
							poblaAbaste =
								true;
					}
					if (
						tipoProyectoID === 10 &&
						(instalacionID === 18 || instalacionID === 56)
					) {
						// REDI REDI
						alcanceProyecto =
							caudalDiseno =
							habitantesEquivalentes =
								true;
						consumoEnergeticoAnual =
							consumoEnergeticoAutoproducido =
							bonifPenalEnergetica =
							iLI =
								true;
					}
					break;
				case 2: // Operación y Mantenimiento
					if (tipoProyectoID === 6 && instalacionID === 13) {
						// IDAM IDAM
						caudalDiseno = true;
						consumoEnergeticoAnual =
							consumoEnergeticoAutoproducido =
							bonifPenalEnergetica =
							usoPriPorc =
							usoPriAguaProd =
							usoSecPorc =
							usoSecAguaProd =
							poblaAbaste =
								true;
					}
					if (
						tipoProyectoID === 5 &&
						(instalacionID === 12 || instalacionID === 31)
					) {
						// ETAP ETAP
						caudalDiseno = true;
						consumoEnergeticoAnual =
							consumoEnergeticoAutoproducido =
							bonifPenalEnergetica =
							usoPriPorc =
							usoPriAguaProd =
							usoSecPorc =
							usoSecAguaProd =
							poblaAbaste =
								true;
					}
					if (
						tipoProyectoID === 4 &&
						(instalacionID === 10 || instalacionID === 29)
					) {
						// EDAR EDAR
						caudalDiseno = habitantesEquivalentes = true;
						consumoEnergeticoAnual =
							consumoEnergeticoAutoproducido =
							bonifPenalEnergetica =
							desnitrificacion =
							digestAnaerobea =
							recuperacionFosforo =
							prodAgua =
							usoPriPorcRege =
							usoPriAguaRege =
								true;
					}
					if (
						tipoProyectoID === 16 &&
						(instalacionID === 63 ||
							instalacionID === 64 ||
							instalacionID === 65)
					) {
						// REDI REDI
						alcanceProyecto =
							caudalDiseno =
							habitantesEquivalentes =
								true;
						consumoEnergeticoAnual =
							consumoEnergeticoAutoproducido =
							bonifPenalEnergetica =
							iLI =
								true;
					}
					break;
				case 3: // Servicios
					if (
						tipoProyectoID === 3 &&
						(instalacionID === 8 || instalacionID === 27)
					) {
						// SRAB OSER
						numAbonados = kmRedAbas = true;
						consumoEnergeticoAnual =
							consumoEnergeticoAutoproducido =
							bonifPenalEnergetica =
							volAguaDist =
							iLI =
								true;
					}
					if (
						tipoProyectoID === 3 &&
						(instalacionID === 9 || instalacionID === 28)
					) {
						// SRAB ETAP
						caudalDiseno = true;
						consumoEnergeticoAnual =
							consumoEnergeticoAutoproducido =
							bonifPenalEnergetica =
							usoPriPorc =
							usoPriAguaProd =
							usoSecAguaProd =
							usoSecPorc =
							poblaAbaste =
								true;
					}
					// if (tipoProyectoID === 3 && instalacionID === 13) {
					//   // SRED EDAR
					//   habitantesEquivalentes = caudalDiseno = true;
					//   consumoEnergeticoAnual =
					//     consumoEnergeticoAutoproducido =
					//     bonifPenalEnergetica =
					//     desnitrificacion =
					//     digestAnaerobea =
					//     recuperacionFosforo =
					//     prodAgua =
					//     volCaudalRecog =
					//       true;
					// }
					if (
						tipoProyectoID === 2 &&
						(instalacionID === 4 || instalacionID === 23)
					) {
						// SRIN OSER
						numAbonados =
							kmRedAbas =
							kmRedSanea =
							habitantesEquivalentes =
								true;
						consumoEnergeticoAnual =
							consumoEnergeticoAutoproducido =
							bonifPenalEnergetica =
							volAguaDist =
							volCaudalRecog =
							iLI =
								true;
					}
					if (
						tipoProyectoID === 2 &&
						(instalacionID === 5 || instalacionID === 24)
					) {
						// SRIN EDAR
						caudalDiseno = habitantesEquivalentes = true;
						consumoEnergeticoAnual =
							consumoEnergeticoAutoproducido =
							bonifPenalEnergetica =
							desnitrificacion =
							digestAnaerobea =
							recuperacionFosforo =
							prodAgua =
							usoPriPorcRege =
							usoPriAguaRege =
								true;
					}
					if (
						tipoProyectoID === 2 &&
						(instalacionID === 7 || instalacionID === 26)
					) {
						// SRIN EBAR
						caudalDiseno = habitantesEquivalentes = true;
						consumoEnergeticoAnual =
							consumoEnergeticoAutoproducido =
							bonifPenalEnergetica =
								true;
					}
					if (
						tipoProyectoID === 2 &&
						(instalacionID === 6 || instalacionID === 25)
					) {
						// SRIN ETAP
						caudalDiseno = true;
						consumoEnergeticoAnual =
							consumoEnergeticoAutoproducido =
							bonifPenalEnergetica =
							usoPriPorc =
							usoPriAguaProd =
							usoSecPorc =
							usoSecAguaProd =
							poblaAbaste =
								true;
					}
					if (
						tipoProyectoID === 1 &&
						(instalacionID === 1 || instalacionID === 20)
					) {
						// SRSA OSER
						habitantesEquivalentes = kmRedSanea = true;
						consumoEnergeticoAnual =
							consumoEnergeticoAutoproducido =
							bonifPenalEnergetica =
							volCaudalRecog =
								true;
					}
					if (
						tipoProyectoID === 1 &&
						(instalacionID === 3 || instalacionID === 22)
					) {
						// SRSA EBAR
						caudalDiseno = habitantesEquivalentes = true;
						consumoEnergeticoAnual =
							consumoEnergeticoAutoproducido =
							bonifPenalEnergetica =
								true;
					}
					if (
						tipoProyectoID === 1 &&
						(instalacionID === 2 || instalacionID === 21)
					) {
						// SRSA EDAR
						caudalDiseno = habitantesEquivalentes = true;
						consumoEnergeticoAnual =
							consumoEnergeticoAutoproducido =
							bonifPenalEnergetica =
							desnitrificacion =
							digestAnaerobea =
							recuperacionFosforo =
							prodAgua =
							usoPriPorcRege =
							usoPriAguaRege =
								true;
					}
					if (
						tipoProyectoID === 14 &&
						(instalacionID === 36 || instalacionID === 37)
					) {
						// SRET ETAP
						caudalDiseno = true;
						consumoEnergeticoAnual =
							consumoEnergeticoAutoproducido =
							bonifPenalEnergetica =
							usoPriPorc =
							usoPriAguaProd =
							usoSecPorc =
							usoSecAguaProd =
							poblaAbaste =
								true;
					}
					if (
						tipoProyectoID === 13 &&
						(instalacionID === 34 || instalacionID === 35)
					) {
						// SRED EDAR
						caudalDiseno = habitantesEquivalentes = true;
						consumoEnergeticoAnual =
							consumoEnergeticoAutoproducido =
							bonifPenalEnergetica =
							desnitrificacion =
							digestAnaerobea =
							recuperacionFosforo =
							prodAgua =
							usoPriPorcRege =
							usoPriAguaRege =
								true;
					}
					break;
				default:
			}

			// Volcado de los flags al modelo de visibilidad (claves = nombres canónicos).
			const flags: Record<string, boolean> = {
				alcanceProyecto,
				caudalDiseno,
				numAbonados,
				iLI,
				kmRedAbas,
				kmRedSanea,
				habitantesEquivalentes,
				consumoEnergeticoAnual,
				consumoEnergeticoAutoproducido,
				bonifPenalEnergetica,
				desnitrificacion,
				digestAnaerobea,
				recuperacionFosforo,
				prodAgua,
				volAguaDist,
				volCaudalRecog,
				usoPriPorc,
				usoPriAguaProd,
				usoSecPorc,
				usoSecAguaProd,
				usoPriPorcRege,
				usoPriAguaRege,
				poblaAbaste,
			};
			this.visibleInstFields = new Set(
				Object.keys(flags).filter((k) => flags[k]),
			);
		} else {
			this.visibleInstFields = new Set<string>();
		}
	}

	initFieldsAndActivitySelector(): void {
		this.showHideInstallationFields();
	}

	/** Las cards llaman aquí antes de tener datos: estado limpio = nada visible. */
	hideAllOperacionDisenoFields(): void {
		this.visibleInstFields = new Set<string>();
	}


	private navigateToNextTab(currentTabIndex: number | undefined): void {
		if (
			currentTabIndex !== undefined &&
			currentTabIndex < this.tabGroup?._tabs.length - 1
		) {
			this.tabGroup.selectedIndex = currentTabIndex + 1;
		} else {
			this.router.navigate(["/home"]);
		}
	}

	private navigateToBackTab(currentTabIndex: number | undefined): void {
		if (currentTabIndex !== undefined && currentTabIndex == 0) {
			// El padre navegable del centro es su contrato; cebesimple solo existe en el alta
			// desde CeBe (/sites/new/:cebeId). Fallback: el listado de sites.
			if (this.contratoPadre) {
				this.router.navigate([`/contracts/${this.contratoPadre.id}`]);
			} else if (this.cebesimple) {
				this.router.navigate([`/profit-centers/${this.cebesimple.id}`]);
			} else {
				this.router.navigate(["/sites"]);
			}
		} else {
			this.tabGroup.selectedIndex = currentTabIndex - 1;
		}
	}

	private initCreateMode() {
		this.centro = {} as CentrosViewModel;
		this.mainFormGroup = this.initMainFormGroup();
	}

	private mapFormValuesToModel(formValues: any): CentrosViewModel {
		const contratosCentrosDropDownListSelected: number[] = [];
		if (this.cebeControl.value !== undefined) {
			contratosCentrosDropDownListSelected.push(this.cebeControl.value);
		}

		const result: CentrosViewModel = {
			id: formValues.id,
			codigo: formValues.codigo,
			edicionCentro: formValues.edicionCentro,
			validacionCentro: formValues.validacionCentro,
			responsableID: this.responsableControl.value?.id,

			descripcion: this.centroDetailData?.get("descripcion")?.value,
			activo: this.centroDetailData?.get("activo")?.value,
			localizacionID: this.centroDetailData?.get("localizacionID")?.value,
			geografiaID: this.centroDetailData?.get("geografiaID")?.value,
			ciudad: this.centroDetailData?.get("ciudad")?.value,
			latitud: this.centroDetailData?.get("latitud")?.value,
			longitud: this.centroDetailData?.get("longitud")?.value,
			tipoInstalacionID:
				this.centroDetailData?.get("tipoInstalacionID")?.value,
			centroActividadID:
				this.centroDetailData?.get("centroActividadID")?.value,
			imageDataB64: this.centroDetailData?.get("imageDataB64")?.value,

			responsableReporteID: this.responsableReporteControl.value?.id,
			introductorAmbientalID: this.introductorAmbientalControl.value?.id,
			introductorSocialID: this.introductorSocialControl.value?.id,
			validadorPrincipalID: this.validadorPrincipalControl.value?.id,
			validadorApoyoID: this.validadorApoyoControl.value?.id,

			alcancePryID: this.centroDesignData?.get("alcancePryID")?.value,
			caudalDiseno: this.centroDesignData?.get("caudalDiseno")?.value,
			habitantesEquivalentes: this.centroDesignData?.get(
				"habitantesEquivalentes",
			)?.value,
			numeroAbonados: this.centroDesignData?.get("numeroAbonados")?.value,
			kmRedAbas: this.centroDesignData?.get("kmRedAbas")?.value,
			kmRedSanea: this.centroDesignData?.get("kmRedSanea")?.value,

			cecoaCentro: this.centroOperationData?.get("cecoaCentro")?.value,
			digestionAnaerobiaFangoID: this.centroOperationData?.get(
				"digestionAnaerobiaFangoID",
			)?.value,
			primaIncluidaID:
				this.centroOperationData?.get("primaIncluidaID")?.value,
			conEnerAn: this.centroOperationData?.get("conEnerAn")?.value,
			conEnerAnAut: this.centroOperationData?.get("conEnerAnAut")?.value,
			producAgua: this.centroOperationData?.get("producAgua")?.value,
			recupFosforoID:
				this.centroOperationData?.get("recupFosforoID")?.value,
			voluRecoSan: this.centroOperationData?.get("voluRecoSan")?.value,
			usoPriPorc: this.centroOperationData?.get("usoPriPorc")?.value,
			usoPriAguaProdID:
				this.centroOperationData?.get("usoPriAguaProdID")?.value,
			usoPriPorcRege:
				this.centroOperationData?.get("usoPriPorcRege")?.value,
			usoPriAguaRegeID:
				this.centroOperationData?.get("usoPriAguaRegeID")?.value,
			usoSecPorc: this.centroOperationData?.get("usoSecPorc")?.value,
			usoSecAguaProdID:
				this.centroOperationData?.get("usoSecAguaProdID")?.value,
			poblaAbaste: this.centroOperationData?.get("poblaAbaste")?.value,
			introductorGeneralID: this.centroOperationData?.get(
				"introductorGeneralID",
			)?.value,
			desnitrificacionID:
				this.centroOperationData?.get("desnitrificacionID")?.value,
			iLI: this.centroOperationData?.get("iLI")?.value,
			volumenFacturado:
				this.centroOperationData?.get("volumenFacturado")?.value,

			fechaValidacion: formValues.centroUserReport.fechaValidacion,
			fechaListoMigra: this.centro?.fechaListoMigra,
			fechaModificacion: this.centro?.fechaModificacion,

			contratosCentrosDropDownListSelected,
		};
		return result;
	}

	initMainFormGroup(): FormGroup {
		return this.fb.group({
			id: [this.centro?.id],
			codigo: [this.centro?.codigo],
			edicionCentro: [this.centro?.edicionCentro],
			validacionCentro: [this.centro?.validacionCentro],
			centroDetails: this.fb.group({
				cebe: this.cebeControl,
				responsable: this.responsableControl,
			}),
			centroUserReport: this.fb.group({
				responsableReporte: this.responsableReporteControl,
				introductorAmbiental: this.introductorAmbientalControl,
				introductorSocial: this.introductorSocialControl,
				validadorPrincipal: this.validadorPrincipalControl,
				validadorApoyo: this.validadorApoyoControl,
				fechaValidacion: [this.centro?.fechaValidacion],
			}),
		});
	}
}
